import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Combobox, DropdownButton } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { segmentStringBySearchTerm } from "app/pages/sales-intelligence/utils/strings";

import { ScrollDismissBoundary } from "../../scroll-dismiss/ScrollDismissBoundary";
import { StyledBaseDropdownButtonWrap } from "../../styles";
import { StyledHighlightFragment } from "../StyledHighlightFragment";

import { resolveSmartSelectedTabId } from "./smartTabs";
import type { TabbedSelectComboboxItem, TabbedSelectComboboxTab } from "./types";

type TabbedSelectComboboxProps<TItem extends TabbedSelectComboboxItem> = {
    tabs: readonly TabbedSelectComboboxTab<TItem>[];
    // Per-item click adapter. The consumer owns the selection model (cascading, select-all, tri-state)
    // and decides what a click means — the combobox only reports which item was pressed.
    onItemClick: (item: TItem) => void;
    // Controlled search, owned by the consumer's useDropdownTabbedSearch (it pre-filters tab items and
    // bakes per-tab counts into tab names), so the combobox does NOT filter — it only renders.
    searchTerm: string;
    onSearchChange: (searchTerm: string) => void;
    searchPlaceholder?: string;
    buttonText: string;
    disabled?: boolean;
    dataAutomation?: string;
};

// Combobox.Option = react-aria ListBoxItem; its style targets `&:has([data-automation-checked="true"])`
// so a manually-driven OptionCheckbox renders correctly even in virtualized mode. The checkbox VISUAL
// is driven by the ITEM (isSelected/partiallySelected), NOT by react-aria selection state.
const renderTabbedOption = <TItem extends TabbedSelectComboboxItem>(
    item: TItem,
    searchTerm: string,
    onItemClick: (item: TItem) => void,
): ReactNode => {
    const fragments = segmentStringBySearchTerm(item.name, searchTerm);

    return (
        <Combobox.Option
            key={item.name}
            id={item.name}
            textValue={item.name}
            isDisabled={item.isDisabled}
            onClick={() => onItemClick(item)}
        >
            <Flex
                alignItems="center"
                justifyContent="space-between"
                gap="8px"
                width="100%"
            >
                <Combobox.OptionLabel>
                    {fragments.map((fragment, index) => (
                        <StyledHighlightFragment
                            key={`${index}-${fragment.text}`}
                            $highlighted={fragment.highlighted}
                        >
                            {fragment.text}
                        </StyledHighlightFragment>
                    ))}
                </Combobox.OptionLabel>
                <Combobox.OptionCheckbox
                    isChecked={Boolean(item.isSelected)}
                    halfSelected={item.partiallySelected}
                    isDisabled={item.isDisabled}
                />
            </Flex>
        </Combobox.Option>
    );
};

/**
 * Virtualized, tabbed multi-select Combobox for the large app-category subcategory lists. Replaces the
 * legacy `TabbedDropdown` + `DropdownSmartTabs` "All" branch. Each tab panel body is a VIRTUALIZED
 * `Combobox.Content` (items + renderItem) so only the visible rows mount — the subcategory lists are
 * large and must stay virtualized.
 *
 * Selection path: `selectionMode="none"` on the virtualized Content so react-aria does NOT intercept
 * clicks for native selection; each `Combobox.Option` carries `onClick` → `onItemClick(item)`. The
 * checkbox VISUAL is driven entirely by the item (`isSelected`/`partiallySelected`), keeping the
 * consumer's custom select-all + cascading + tri-state model intact.
 *
 * Smart tabs: the active tab is controlled — when the current tab empties out (after a search) we move
 * to the first tab with results (port of `withDropdownSmartTabs`); empty tabs are disabled.
 *
 * NOTE: the virtualized rendering path (Virtualizer + ListLayout) only materializes under a real
 * layout, so it needs browser verification — jsdom does not lay the rows out.
 */
const TabbedSelectCombobox = <TItem extends TabbedSelectComboboxItem>({
    tabs,
    onItemClick,
    searchTerm,
    onSearchChange,
    searchPlaceholder,
    buttonText,
    disabled = false,
    dataAutomation,
}: TabbedSelectComboboxProps<TItem>) => {
    const { translate } = useTranslate();
    const [isOpen, setIsOpen] = useState(false);
    const [selectedTabId, setSelectedTabId] = useState<string | undefined>(() => tabs[0]?.id);

    if (disabled && isOpen) {
        setIsOpen(false);
    }

    // Keep the active tab on a tab that has results (smart tabs). Runs on every tab change so a search
    // that empties the current tab hops forward, mirroring the legacy HOC's effect.
    useEffect(() => {
        setSelectedTabId((current) => resolveSmartSelectedTabId(tabs, current));
    }, [tabs]);

    const handleOpenChange = useCallback(
        (open: boolean) => {
            if (disabled && open) {
                return;
            }

            setIsOpen(open);
        },
        [disabled],
    );

    const handleTabSelect = useCallback((key: string | number) => {
        setSelectedTabId(String(key));
    }, []);

    const handleItemClick = useCallback(
        (item: TItem) => {
            onItemClick(item);
        },
        [onItemClick],
    );

    const emptyState = useMemo(
        () => <Combobox.OptionLabel>{translate("dropdown.no.results")}</Combobox.OptionLabel>,
        [translate],
    );

    return (
        <ScrollDismissBoundary
            isOpen={isOpen}
            onClose={() => handleOpenChange(false)}
        >
            <Combobox
                isOpen={isOpen}
                onOpenChange={handleOpenChange}
                placement="bottom-start"
                data-automation={dataAutomation}
            >
                <Combobox.Trigger>
                    <StyledBaseDropdownButtonWrap disabled={disabled}>
                        <DropdownButton disabled={disabled}>{buttonText}</DropdownButton>
                    </StyledBaseDropdownButtonWrap>
                </Combobox.Trigger>

                <Combobox.SearchInput
                    value={searchTerm}
                    onChange={onSearchChange}
                    placeholder={searchPlaceholder}
                />

                <Combobox.Tabs
                    selectedKey={selectedTabId}
                    onSelectionChange={handleTabSelect}
                >
                    <Combobox.TabList>
                        {tabs.map((tab) => (
                            <Combobox.Tab
                                key={tab.id}
                                id={tab.id}
                                isDisabled={tab.items.length === 0}
                            >
                                {tab.name}
                            </Combobox.Tab>
                        ))}
                    </Combobox.TabList>

                    {tabs.map((tab) => (
                        <Combobox.TabPanel
                            key={tab.id}
                            id={tab.id}
                        >
                            <Combobox.Content<TItem>
                                virtualized
                                selectionMode="none"
                                items={tab.items as TItem[]}
                                renderItem={(item) => renderTabbedOption(item, searchTerm, handleItemClick)}
                                emptyState={emptyState}
                            />
                        </Combobox.TabPanel>
                    ))}
                </Combobox.Tabs>
            </Combobox>
        </ScrollDismissBoundary>
    );
};

export default TabbedSelectCombobox;
